/**
 * Named, saved Insights views — persisted to localStorage, keyed by page
 * namespace + active impersonation domain (mirrors filterPersistence). A "view"
 * is a data slice (filters + criteria + score + group-by + window) with an id
 * and a name, so a DG can save "Enterprise tier grouped by customer" and jump
 * back to it. Server-side and team-published views are a follow-up.
 *
 * Durable storage is only safe now that a window carries intent rather than
 * resolved dates: a view kept for months would otherwise silently rot. It also
 * brings two things per-tab storage made free. Stored views now outlive
 * deploys, so the shape is version-stamped and a mismatch is dropped rather
 * than cast blindly into a render. And two tabs can each hold their own copy,
 * so writes are observed (see the `storage` listener in SavedViewTabs) instead
 * of silently clobbering one another.
 *
 * A view is *what* you're looking at, never *how* you're looking at it. The
 * plotted metric is display state: it is excluded from the slice
 * (`toViewSlice`), and therefore from a view's identity (`viewSignature`,
 * derived from the slice) and from what applying one overwrites
 * (`resolveView`) — so switching metric never un-highlights the view you are
 * in, and clicking the already-active tab is always a no-op.
 *
 * The window used to be excluded for the same reason, but only because it was
 * stored as resolved dates: a view saved as `Aug 5 → Aug 12` stopped matching
 * the next day though nothing about the intent had changed. Now that it carries
 * intent (see utils/dateWindow), `{rolling, 7, day}` has the same signature
 * tomorrow that it has today, so it belongs in the slice — which is what makes
 * a 7-day and a 30-day view of the same filters two distinguishable views.
 */
import type { PersistedFilterState } from "./filterPersistence";
import { windowKey } from "./dateWindow";
import { getImpersonationDomain } from "../../impersonation/storage";

export interface SavedView extends PersistedFilterState {
  id: string;
  name: string;
  /** Server-side only: whether the tenant can see it. Absent for local views. */
  /** Server-side only: the Cognito username of the author. */
  owner?: string;
}

const KEY_PREFIX = "composo:insights-views";

/**
 * Bumped when the stored shape changes incompatibly. Anything stamped with a
 * version this build doesn't know is dropped rather than cast — the cast was a
 * tolerable edge while views died with the session, but a shape change now
 * reaches storage that has been sitting on disk across a deploy.
 */
const STORAGE_VERSION = 1;

interface StoredEnvelope {
  version: number;
  views: SavedView[];
}

export interface SavedViewStore {
  views: SavedView[];
  /** True when something was stored but this build couldn't read it. */
  dropped: boolean;
}

export function storageKey(namespace: string): string {
  const domain = getImpersonationDomain() || "self";
  return `${KEY_PREFIX}:${namespace}:${domain}`;
}

function isSavedView(value: unknown): value is SavedView {
  const v = value as Partial<SavedView> | null;
  return (
    !!v &&
    typeof v === "object" &&
    typeof v.id === "string" &&
    typeof v.name === "string" &&
    Array.isArray(v.filters)
  );
}

/**
 * All saved views for the active scope.
 *
 * `dropped` distinguishes "you never saved any" from "yours could not be
 * read" so the strip can say so, rather than a list silently emptying itself.
 */
export function readSavedViews(namespace: string): SavedViewStore {
  const empty = { views: [], dropped: false };
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(storageKey(namespace));
  } catch {
    return empty;
  }

  // One-time carry-over for views saved while this was session-scoped, so
  // upgrading mid-session doesn't look like the feature lost your work.
  if (!raw) {
    const migrated = migrateFromSession(namespace);
    return migrated ? { views: migrated, dropped: false } : empty;
  }

  try {
    const parsed = JSON.parse(raw) as StoredEnvelope | SavedView[];
    // Pre-envelope writes were a bare array; adopt them at the current version.
    const envelope: StoredEnvelope = Array.isArray(parsed)
      ? { version: STORAGE_VERSION, views: parsed }
      : parsed;
    if (envelope?.version !== STORAGE_VERSION || !Array.isArray(envelope.views)) {
      return { views: [], dropped: true };
    }
    // Filter rather than trust: one malformed entry would otherwise throw
    // inside viewSignature and take the page down with it.
    const views = envelope.views.filter(isSavedView);
    return { views, dropped: views.length !== envelope.views.length };
  } catch {
    return { views: [], dropped: true };
  }
}

function migrateFromSession(namespace: string): SavedView[] | null {
  try {
    const raw = window.sessionStorage.getItem(storageKey(namespace));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const views = parsed.filter(isSavedView);
    if (views.length) writeSavedViews(namespace, views);
    window.sessionStorage.removeItem(storageKey(namespace));
    return views;
  } catch {
    return null;
  }
}

/** Persist the view list; no-ops if storage is unavailable. */
export function writeSavedViews(namespace: string, views: SavedView[]): void {
  try {
    const envelope: StoredEnvelope = { version: STORAGE_VERSION, views };
    window.localStorage.setItem(storageKey(namespace), JSON.stringify(envelope));
  } catch {
    /* quota / private-mode / SSR — degrade to non-persistent for this session */
  }
}

/** A collision-resistant id, with a fallback where crypto.randomUUID is absent. */
export function newViewId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `v-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
}

/**
 * The part of a filter state a view actually is: the data slice. Whitelisted
 * rather than subtracted, so display state (scoreMetric) and the view's own
 * id/name can never leak in by being spread from a caller.
 */
export function toViewSlice(state: PersistedFilterState): PersistedFilterState {
  const { filters, criteriaFilter, scoreFilter, groupByKey, dateWindow } = state;
  return {
    filters: filters ?? [],
    criteriaFilter,
    scoreFilter,
    groupByKey,
    dateWindow,
  };
}

/**
 * The state to apply when a view is clicked: the view's slice, plus the display
 * state you are currently on.
 *
 * The window now comes from the view — it is part of the slice — but because it
 * is stored as *intent* it still resolves against today, so a rolling view is
 * current every time it is opened. A view saved before windows were part of a
 * slice has none, and falls back to the window you are on, which is exactly
 * what it did when it was saved.
 */
export function resolveView(
  view: SavedView,
  current: PersistedFilterState,
): PersistedFilterState {
  const slice = toViewSlice(view);
  return {
    ...slice,
    dateWindow: slice.dateWindow ?? current.dateWindow,
    scoreMetric: current.scoreMetric,
  };
}

/**
 * Normalised signature of a view's data slice, for active-tab detection.
 * Derived from `toViewSlice`, so it ignores display state by construction.
 * Filters (and their values) are sorted so order can't cause a false mismatch.
 */
export function viewSignature(state: PersistedFilterState): string {
  const { filters, criteriaFilter, scoreFilter, groupByKey, dateWindow } =
    toViewSlice(state);
  return JSON.stringify({
    window: windowKey(dateWindow),
    filters: [...(filters ?? [])]
      .map((f) => ({ key: f.key, values: [...f.values].sort() }))
      .sort((a, b) => a.key.localeCompare(b.key)),
    criteria: [...(criteriaFilter?.values ?? [])].sort(),
    score: scoreFilter ?? null,
    groupByKey: groupByKey ?? null,
  });
}
